// ─────────────────────────────────────────────────────────────
// MemoService — memos 테이블 책임 격리
// 공유 메모 코르크보드 (배송시트지 메모 탭)
// optimistic lock 패턴: update 시 updated_at 일치 조건 사용
// ─────────────────────────────────────────────────────────────
import { supabase } from '../supabase';

export const MemoService = {
  // 활성(미완료) 메모 전체 — pinned/sort_order/created_at 정렬 (v7.50)
  // 호출자가 클라이언트 측에서 completed_at 필터 처리 (마이그레이션 호환)
  async listActive() {
    const { data, error } = await supabase.from('memos')
      .select('*')
      .order('pinned', { ascending: false })
      .order('created_at', { ascending: false });
    if (error) return { ok: false, error, data: [] };
    const filtered = (data || []).filter(m => !m.completed_at);
    // v7.50: sort_order 우선 정렬 (NULL은 fallback으로 created_at 유지)
    filtered.sort((a, b) => {
      // pinned 우선
      if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
      const aHas = a.sort_order != null;
      const bHas = b.sort_order != null;
      if (aHas && bHas) return a.sort_order - b.sort_order;
      if (aHas) return -1; // sort_order 있는 게 먼저
      if (bHas) return 1;
      // 둘 다 NULL: created_at desc 유지 (서버 정렬 결과 보존)
      return 0;
    });
    return { ok: true, data: filtered };
  },

  // 완료된 메모 (특정 월)
  async listCompleted(monthDate) {
    const y = monthDate.getFullYear();
    const m = monthDate.getMonth();
    const start = `${y}-${String(m + 1).padStart(2, '0')}-01`;
    const endD = new Date(y, m + 1, 0);
    const end = `${y}-${String(m + 1).padStart(2, '0')}-${String(endD.getDate()).padStart(2, '0')}T23:59:59`;
    const { data, error } = await supabase.from('memos')
      .select('*')
      .not('completed_at', 'is', null)
      .gte('completed_at', start)
      .lte('completed_at', end)
      .order('completed_at', { ascending: false });
    if (error) return { ok: false, error, data: [] };
    return { ok: true, data: data || [] };
  },

  // v7.450: 완료 메모 전체 검색 (내용·작성자) — 월 무관
  async searchCompleted(query) {
    const q = (query || '').trim();
    if (!q) return { ok: true, data: [] };
    const esc = q.replace(/[,()%]/g, ' ').trim(); // PostgREST .or 구문 안전
    if (!esc) return { ok: true, data: [] };
    const { data, error } = await supabase.from('memos')
      .select('*')
      .not('completed_at', 'is', null)
      .or(`content.ilike.%${esc}%,author.ilike.%${esc}%`)
      .order('completed_at', { ascending: false })
      .limit(200);
    if (error) return { ok: false, error, data: [] };
    return { ok: true, data: data || [] };
  },

  // 신규 메모 추가
  // returns: { ok, data, msg }
  // v7.190: importance (0~5 별점) 지원 — 미지정 시 0
  async create({ content, color, author, importance = 0 }) {
    const { data, error } = await supabase.from('memos')
      .insert({ content, color, author: author || null, importance: Number(importance) || 0 })
      .select().single();
    if (error) return { ok: false, msg: '메모 추가 실패: ' + error.message };
    return { ok: true, data };
  },

  // 메모 내용 수정 (optimistic lock — updated_at 일치 조건)
  // returns: { ok, data, conflict, msg }
  async updateContent(id, content, updatedAt) {
    const { data, error } = await supabase.from('memos')
      .update({ content })
      .eq('id', id).eq('updated_at', updatedAt)
      .select().single();
    if (error) return { ok: false, conflict: true, msg: error.message };
    return { ok: true, data };
  },

  // 메모 필드 수정 (color, pinned 등) — optimistic lock
  async updateFields(id, patch, updatedAt) {
    const { data, error } = await supabase.from('memos')
      .update(patch)
      .eq('id', id).eq('updated_at', updatedAt)
      .select().single();
    if (error) return { ok: false, conflict: true, msg: error.message };
    return { ok: true, data };
  },

  // 메모 삭제
  async remove(id) {
    const { error } = await supabase.from('memos').delete().eq('id', id);
    if (error) return { ok: false, msg: '삭제 실패: ' + error.message };
    return { ok: true };
  },

  // 완료 처리
  // returns: { ok, msg, needsMigration }
  async complete(id) {
    const now = new Date().toISOString();
    const { error } = await supabase.from('memos')
      .update({ completed_at: now })
      .eq('id', id);
    if (error) {
      const needsMigration = error.message && error.message.includes('completed_at');
      return { ok: false, msg: error.message, needsMigration };
    }
    return { ok: true };
  },

  // 완료 복원
  async restore(id) {
    const { error } = await supabase.from('memos')
      .update({ completed_at: null })
      .eq('id', id);
    if (error) return { ok: false, msg: '복원 실패: ' + error.message };
    return { ok: true };
  },

  // 두 메모의 sort_order 교환 (드래그앤드롭) — Promise.all 병렬 실행
  async swapSortOrder(idA, orderA, idB, orderB) {
    try {
      await Promise.all([
        supabase.from('memos').update({ sort_order: orderA }).eq('id', idA),
        supabase.from('memos').update({ sort_order: orderB }).eq('id', idB),
      ]);
      return { ok: true };
    } catch (e) {
      return { ok: false, msg: e.message };
    }
  },

  // v7.50: 일괄 sort_order 갱신 — drag-and-drop insert 방식에서 전체 재할당용
  // items: [{ id, sort_order }, ...]
  async updateSortOrders(items) {
    try {
      await Promise.all(items.map(({ id, sort_order }) =>
        supabase.from('memos').update({ sort_order }).eq('id', id)
      ));
      return { ok: true };
    } catch (e) {
      return { ok: false, msg: e.message };
    }
  },
};
